import {useNavigate} from 'react-router-dom';

import {
	AuthorizationsEnum,
	ContractTypeEnum,
	EmergencyTelehealthEnum,
	ExamBookingEnum,
	FinancialPendencyEnum,
	HealthBookingEnum,
	OdontoBookingEnum,
	OperationalCostEnum,
	TelehealthBookingEnum,
} from 'presentation/constants';

import {ServicesTypesEnum} from 'presentation/pages/private/services/types';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {useOperationalCostPassword} from './useOperationalCostPassword';

export function useServicePermission() {
	const navigate = useNavigate();
	const {
		isOperationalCostAuthorized,
		setOperationStatePass,
		IS_SUBMITTING_PASS,
		IS_ERROR_PASS,
		setOperationStateUrl,
		IS_SUBMITTING_GET_URL,
		IS_ERROR_GET_URL,
		passwordErrorMessage,
	} = useOperationalCostPassword();

	const {sidebar, beneficiary} = useAccountDataStore(state => state.accountData);

	const BUSINESS_PLAN = beneficiary.tipoContratacao === ContractTypeEnum.EMPRESARIAL;

	const FINANCIAL_PENDENCY_PLAN = BUSINESS_PLAN && sidebar.flUsuarioInadimplente;
	const OPERATIONAL_COST_BLOCK =
		BUSINESS_PLAN && sidebar.ctOperacional && sidebar.ctOperacional.permitirMarcacao === false;
	const OPERATIONAL_COST_BLOCK_NEED_PASS =
		BUSINESS_PLAN && sidebar.ctOperacional && sidebar.ctOperacional?.exigeSenha;

	function handleFinancialPendency(type: ServicesTypesEnum) {
		navigate(FinancialPendencyEnum.FINANCIAL_PENDENCY, {
			state: {
				type,
			},
		});
	}

	function handleOperationalCost(needPass: boolean, type: ServicesTypesEnum) {
		if (needPass) {
			navigate(OperationalCostEnum.OPERATIONAL_COST_NEED_PASSWORD, {
				state: {
					type,
				},
			});
			return;
		}

		navigate(OperationalCostEnum.OPERATIONAL_COST, {
			state: {
				type,
			},
		});
	}

	function canBookHealthService() {
		if (FINANCIAL_PENDENCY_PLAN) {
			handleFinancialPendency(ServicesTypesEnum.HEALTH);
			return false;
		}

		if (OPERATIONAL_COST_BLOCK) {
			handleOperationalCost(false, ServicesTypesEnum.HEALTH);
			return false;
		}

		if (OPERATIONAL_COST_BLOCK_NEED_PASS) {
			handleOperationalCost(true, ServicesTypesEnum.HEALTH);
			return false;
		}

		return true;
	}

	function canBookTelehealthService() {
		if (FINANCIAL_PENDENCY_PLAN) {
			handleFinancialPendency(ServicesTypesEnum.TELEHEALTH);
			return false;
		}

		if (OPERATIONAL_COST_BLOCK) {
			handleOperationalCost(false, ServicesTypesEnum.TELEHEALTH);
			return false;
		}

		if (OPERATIONAL_COST_BLOCK_NEED_PASS) {
			handleOperationalCost(true, ServicesTypesEnum.TELEHEALTH);
			return false;
		}

		return true;
	}

	function canBookOdontothService() {
		if (FINANCIAL_PENDENCY_PLAN) {
			handleFinancialPendency(ServicesTypesEnum.ODONTO);
			return false;
		}

		return true;
	}

	function canBookExamsService() {
		if (FINANCIAL_PENDENCY_PLAN) {
			handleFinancialPendency(ServicesTypesEnum.EXAMS);
			return false;
		}

		if (OPERATIONAL_COST_BLOCK) {
			handleOperationalCost(false, ServicesTypesEnum.EXAMS);
			return false;
		}

		if (OPERATIONAL_COST_BLOCK_NEED_PASS) {
			handleOperationalCost(true, ServicesTypesEnum.EXAMS);
			return false;
		}

		return true;
	}

	function canAccessEmergencyTelehealthService() {
		if (FINANCIAL_PENDENCY_PLAN) {
			handleFinancialPendency(ServicesTypesEnum.EMERGENCY_TELEHEALTH);
			return false;
		}

		if (OPERATIONAL_COST_BLOCK) {
			handleOperationalCost(false, ServicesTypesEnum.EMERGENCY_TELEHEALTH);
			return false;
		}

		if (OPERATIONAL_COST_BLOCK_NEED_PASS) {
			handleOperationalCost(true, ServicesTypesEnum.EMERGENCY_TELEHEALTH);
			return false;
		}

		return true;
	}

	function canAccessAuthorizationsService() {
		if (OPERATIONAL_COST_BLOCK) {
			handleOperationalCost(false, ServicesTypesEnum.AUTHORIZATIONS);
			return false;
		}

		if (OPERATIONAL_COST_BLOCK_NEED_PASS) {
			handleOperationalCost(true, ServicesTypesEnum.AUTHORIZATIONS);
			return false;
		}

		return true;
	}

	function canOpenService(service: ServicesTypesEnum) {
		const serviceAccessMap: Record<ServicesTypesEnum, () => boolean> = {
			[ServicesTypesEnum.HEALTH]: canBookHealthService,
			[ServicesTypesEnum.TELEHEALTH]: canBookTelehealthService,
			[ServicesTypesEnum.ODONTO]: canBookOdontothService,
			[ServicesTypesEnum.EXAMS]: canBookExamsService,
			[ServicesTypesEnum.EMERGENCY_TELEHEALTH]: canAccessEmergencyTelehealthService,
			[ServicesTypesEnum.AUTHORIZATIONS]: canAccessAuthorizationsService,
			[ServicesTypesEnum.OTHERS]: () => true,
		};

		const accessService = serviceAccessMap[service];
		return accessService();
	}

	function serviceTypeByPath(path: string) {
		if (path.includes(HealthBookingEnum.HEALTH_BOOKING_FLOW)) {
			return ServicesTypesEnum.HEALTH;
		}

		if (path.includes(OdontoBookingEnum.ODONTO_BOOKING_FLOW)) {
			return ServicesTypesEnum.ODONTO;
		}

		if (path.includes(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW)) {
			return ServicesTypesEnum.TELEHEALTH;
		}

		if (path.includes(EmergencyTelehealthEnum.EMERGENCY_TELEHEALTH_FLOW)) {
			return ServicesTypesEnum.EMERGENCY_TELEHEALTH;
		}

		if (path.includes(ExamBookingEnum.EXAM_BOOKING_FLOW)) {
			return ServicesTypesEnum.EXAMS;
		}

		if (path.includes(AuthorizationsEnum.AUTHORIZATIONS)) {
			return ServicesTypesEnum.AUTHORIZATIONS;
		}

		return ServicesTypesEnum.OTHERS;
	}

	return {
		setOperationStatePass,
		IS_SUBMITTING_PASS,
		IS_ERROR_PASS,
		isOperationalCostAuthorized,
		passwordErrorMessage,
		canOpenService,
		serviceTypeByPath,
		setOperationStateUrl,
		IS_SUBMITTING_GET_URL,
		IS_ERROR_GET_URL,
	};
}
